import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReplaySubject, first, firstValueFrom } from 'rxjs';
import {
  ApiResponse,
  AuthenticationResponse,
} from '../interfaces/response.interfaces';
import { ApiService } from './api.service';
import { DialogService } from './dialog.service';
import { WebSocketService } from './websocket.service';
import { RESOURCES } from '../constants/resources.constants';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  resources = RESOURCES;
  private prefix = 'keep';
  public permisos = {
    VENDEDOR: 'apps.ventas.vendedor',
    VALIDADOR_PREVENTA: 'apps.ventas.validador_preventa',
    VALIDADOR_POSTVENTA: 'apps.ventas.validador_postventa',
    PUEDE_USAR_CORTESIA: 'apps.ventas.puede_usar_cortesia',
    LEADS: 'apps.ventas.leads',
  };
  private sessionCheckInterval: any;

  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  private readonly dialogService = inject(DialogService);
  private readonly webSocketService = inject(WebSocketService);
  private refreshSessionInProgress: ReplaySubject<void> | null = null;

  user = signal<any>(null);

  constructor() {
    if (localStorage.getItem('refreshToken')) {
      this.refreshSession();
    }
    // } else {
    //   this.logout();
    // }
  }

  refreshSession() {
    this.apiService
      .post(this.apiService.resources.refreshToken, {
        refreshToken: localStorage.getItem('refreshToken'),
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: ApiResponse<any>) => {
          if (
            res.code !== 200 ||
            !res.authorization ||
            !res.authorization.user
          ) {
            this.logout();
          } else {
            this.guardarToken(res.authorization);
            const permisos = res.authorization?.user.permisos;
            if (permisos) {
              this.guardarPermisos(permisos);
            }
          }
        },
        error: () => {
          this.logout();
        },
      });
  }

  updateSession() {
    if (!this.refreshSessionInProgress) {
      this.refreshSessionInProgress = new ReplaySubject<void>(1);

      this.apiService
        .post(this.apiService.resources.refreshToken, {
          refreshToken: localStorage.getItem('refreshToken'),
        })
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (res: ApiResponse<any>) => {
            if (
              res.code !== 200 ||
              !res.authorization ||
              !res.authorization.user
            ) {
              this.logout();
            } else {
              this.guardarToken(res.authorization);
              const permisos = res.authorization?.user.permisos;
              if (permisos) {
                this.guardarPermisos(permisos);
              }
            }

            this.refreshSessionInProgress?.next();
            this.refreshSessionInProgress?.complete();
            this.refreshSessionInProgress = null;
          },
          error: () => {
            this.refreshSessionInProgress?.next();
            this.refreshSessionInProgress?.complete();
            this.refreshSessionInProgress = null;
          },
        });
    }

    return firstValueFrom(this.refreshSessionInProgress.pipe(first()));
  }

  guardarToken(authPayload: AuthenticationResponse) {
    this.user.set(authPayload.user);
    localStorage.setItem('accessToken', authPayload.accessToken);
    localStorage.setItem('refreshToken', authPayload.refreshToken);
  }

  guardarPermisos(permisos: string[]) {
    const vendedor = permisos.some(
      (element) => element === this.permisos.VENDEDOR
    );
    localStorage.setItem('vendedor', vendedor?.toString() || 'false');
    const isValidadorPre = permisos.some(
      (element) => element === this.permisos.VALIDADOR_PREVENTA
    );
    localStorage.setItem(
      'validadorPreVenta',
      isValidadorPre?.toString() || 'false'
    );
    const isValidadorPost = permisos.some(
      (element) => element === this.permisos.VALIDADOR_POSTVENTA
    );
    localStorage.setItem(
      'validadorPostVenta',
      isValidadorPost?.toString() || 'false'
    );
    const canUseCourtesy = permisos.some(
      (element) => element === this.permisos.PUEDE_USAR_CORTESIA
    );
    localStorage.setItem(
      'canUseCourtesy',
      canUseCourtesy?.toString() || 'false'
    );
    const leads = permisos.some(
      (element) => element === this.permisos.LEADS
    );
    localStorage.setItem('leads', leads?.toString() || 'false'
    );

    // Conectar WebSocket si es validador
    if (isValidadorPre || isValidadorPost) {
      this.webSocketService.connect();
    }
  }
  logout() {
    const alias = this.getUserAlias(); // Obtiene el alias del usuario almacenado
    
    // Desconectar WebSocket
    this.webSocketService.disconnect();
    
    this.apiService
      .post(this.resources.logout, { alias }).subscribe({
        next: () => {
          this.clearStorage();
          this.router.navigateByUrl('/auth');
          this.dialogService.closeAll();
          clearInterval(this.sessionCheckInterval);
        },
        error: (error) => {
          console.error('Error al cerrar sesión:', error);
          // Aún así, limpiar almacenamiento y redirigir
          this.clearStorage();
          this.router.navigateByUrl('/auth');
          this.dialogService.closeAll();
          clearInterval(this.sessionCheckInterval);
        }
      });
  }

  getUserAlias(): string {
    return localStorage.getItem('nombres') || '';
  }

  // logout() {
  //   this.clearStorage();
  //   this.router.navigateByUrl('/auth');
  //   this.dialogService.closeAll();
  // }

  clearStorage() {
    const total = localStorage.length;
    const keysToRemove = [];
    for (let i = 0; i < total; i++) {
      const key = localStorage.key(i);
      if (key && !key.startsWith(this.prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}
